import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Filter, BookOpen, Bookmark, Sparkles, BookMarked, Layers, Headphones } from "lucide-react";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../api/axios";
import tracker from "../../utils/sanctuary-tracker";
import { stripHtml, truncate } from "../../utils/text";

import { CONFIG } from "../../config";
// const API_BASE = CONFIG.API_URL; // Used in axios instance

import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";

export default function ExploreFeed({ userCategories = "", showFilters = true }) {
    const { user } = useAuth();
    const { language, t } = useLanguage();
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(user ? "for-you" : "all");

    const [savedIds, setSavedIds] = useState(new Set());

    useEffect(() => {
        // Load Categories
        api.get("/categories")
            .then((res) => setCategories(res.data))
            .catch(console.error);

        loadArticles();
        loadBookmarks();
    }, []);

    useEffect(() => {
        loadArticles();
    }, [selectedCategory, language]);

    const loadBookmarks = async () => {
        try {
            const res = await api.get("/articles/bookmarks/ids");
            setSavedIds(new Set(res.data));
        } catch (e) { console.error(e); }
    };

    const handleBookmark = async (e, articleId) => {
        e.preventDefault();

        const isSaved = savedIds.has(articleId);
        const next = new Set(savedIds);
        if (isSaved) next.delete(articleId);
        else next.add(articleId);
        setSavedIds(next);

        try {
            const res = await api.post(`/articles/bookmarks/${articleId}`);
            if (res.data.bookmarked) toast.success("Article saved to your library");
            else toast("Article removed from library");
        } catch (err) {
            setSavedIds(savedIds); // Revert
            toast.error("Something went wrong");
        }
    };

    const handleFilterSelect = (catId) => {
        setSelectedCategory(catId);
        
        // 🧘 AYI: Track Category Selection (Gap Detection)
        tracker.track('CATEGORY_SELECT', { categoryId: catId });
    };

    const loadArticles = () => {
        setLoading(true);
        let url = "/articles";

        if (selectedCategory === "library") {
            url = "/articles/bookmarks/list";
        } else if (selectedCategory === "for-you") {
            if (userCategories) url += `?categoryId=${userCategories}`;
        } else if (selectedCategory === "all") {
            // Fetch all
        } else if (selectedCategory) {
            url += `?categoryId=${selectedCategory}`;
        }

        // 🌐 Append Language
        const separator = url.includes("?") ? "&" : "?";
        url += `${separator}lang=${language}`;

        api.get(url)
            .then((res) => {
                if (Array.isArray(res.data)) {
                    setArticles(res.data);
                } else {
                    setArticles([]);
                }
            })
            .catch((err) => {
                console.error("Feed load failed", err);
                if (selectedCategory === "library" && err.response?.status === 401) {
                    toast.error("Please login to see your library");
                }
                setArticles([]);
            })
            .finally(() => setLoading(false));
    };

    return (
        <div className="space-y-6">
            {/* Modern Pill Filters */}
            {showFilters && (
                <div className="flex items-center gap-2 overflow-x-auto pb-4 scrollbar-hide">
                    {/* 1. FOR YOU (Only if logged in) */}
                    {user && (
                        <button
                            onClick={() => handleFilterSelect("for-you")}
                            className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${selectedCategory === "for-you"
                                ? "bg-brand-blue text-white shadow-md"
                                : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                                }`}
                        >
                            <Sparkles className="w-3.5 h-3.5" /> {t('wisdom_for_you')}
                        </button>
                    )}

                    {/* 2. MY LIBRARY (Only if logged in) */}
                    {user && (
                        <button
                            onClick={() => handleFilterSelect("library")}
                            className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${selectedCategory === "library"
                                ? "bg-amber-100 text-amber-800 border border-amber-200 shadow-sm"
                                : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                                }`}
                        >
                            <BookMarked className="w-3.5 h-3.5" /> {t('wisdom_my_library')}
                        </button>
                    )}


                    {/* 3. ALL (Clear filters) */}
                    <button
                        onClick={() => handleFilterSelect("all")}
                        className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all ${selectedCategory === "all"
                            ? "bg-brand-blue text-white shadow-md"
                            : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                            }`}
                    >
                        {t('wisdom_all_topics')}
                    </button>

                    {/* 4. Categories */}
                    {categories.map((c) => (
                        <button
                            key={c.id}
                            onClick={() => handleFilterSelect(c.id)}
                            className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all ${selectedCategory === c.id
                                ? "bg-brand-blue text-white shadow-md"
                                : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                                }`}
                        >
                            {c.label}
                        </button>
                    ))}
                </div>
            )}

            {/* Feed with Animations */}
            <AnimatePresence mode="wait">
                {loading ? (
                    <motion.div
                        key="loader"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="text-center py-20 opacity-50 flex flex-col items-center"
                    >
                        <div className="w-8 h-8 rounded-full border-4 border-gray-100 border-t-brand-blue animate-spin mb-4"></div>
                        <p className="font-display text-lg text-gray-400">{t('wisdom_gathering')}</p>
                    </motion.div>
                ) : articles.length === 0 ? (
                    <motion.div
                        key="empty"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="text-center py-16 bg-white dark:bg-gray-800 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700 text-gray-400"
                    >
                        <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-20" />
                        <p className="text-lg font-medium text-gray-500 dark:text-gray-400">
                            {userCategories ? t('wisdom_path_quiet') : t('wisdom_no_articles')}
                        </p>
                        <p className="text-sm mt-1">
                            {/* Check role from localStorage or context if available, otherwise generic */}
                            {(() => {
                                try {
                                    const user = JSON.parse(localStorage.getItem("user"));
                                    if (user?.role === "PROVIDER") {
                                        return "Be the first to share wisdom on this topic.";
                                    }
                                    return "Wisdom is gathering here. Please check back soon.";
                                } catch {
                                    return "No content available at the moment.";
                                }
                            })()}
                        </p>
                    </motion.div>
                ) : (
                    <motion.div
                        key={selectedCategory || "feed"}
                        initial="hidden"
                        animate="show"
                        exit="hidden"
                        variants={{
                            hidden: { opacity: 0 },
                            show: {
                                opacity: 1,
                                transition: {
                                    staggerChildren: 0.05
                                }
                            }
                        }}
                        className="grid gap-6"
                    >
                        {articles.map((article) => (
                            <motion.article
                                key={article.id}
                                layoutId={`article-${article.id}`}
                                variants={{
                                    hidden: { opacity: 0, y: 20 },
                                    show: { opacity: 1, y: 0 }
                                }}
                                className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow duration-300 relative group"
                            >
                                {/* Bookmark Button */}
                                    {user && (
                                        <button
                                            onClick={(e) => handleBookmark(e, article.id)}
                                            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/90 backdrop-blur shadow-sm hover:bg-white transition"
                                        >
                                            <Bookmark
                                                className={`w-5 h-5 ${savedIds.has(article.id) ? "fill-brand-blue text-brand-blue" : "text-gray-400 hover:text-brand-blue"}`}
                                            />
                                        </button>
                                    )}

                                {article.coverImage && (
                                    <div className="h-48 bg-gray-100 w-full overflow-hidden">
                                        <img
                                            src={article.coverImage}
                                            alt={article.title}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                )}
                                <div className="p-6">
                                    <div className="flex items-center gap-2 mb-3">
                                        <span className="px-3 py-1 bg-blue-50 text-brand-blue text-xs font-semibold rounded-full uppercase tracking-wide flex items-center gap-2">
                                            {article.category?.label || "General"}
                                            {article.audioUrl && <Headphones size={10} />}
                                        </span>
                                        <span className="text-xs text-gray-400">
                                            {new Date(article.createdAt).toLocaleDateString()}
                                        </span>
                                    </div>

                                    <h3 className="text-2xl font-display font-bold text-gray-900 mb-2">
                                        {article.title}
                                    </h3>

                                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                                        <span className="font-medium text-gray-800">
                                            By {article.author?.name || 'All-Seer'}
                                        </span>
                                        {article.author?.isCertified && (
                                            <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded border border-green-200">
                                                Certified
                                            </span>
                                        )}
                                    </div>

                                    <div className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3 leading-relaxed mb-4">
                                        {truncate(stripHtml(article.excerpt || article.content), 180)}
                                    </div>

                                    <Link
                                        to={`/article/${article.slug || article.id}`}

                                        className="mt-4 text-brand-blue font-medium hover:underline text-sm inline-block"
                                    >
                                        {t("wisdom_read_more")}
                                    </Link>
                                </div>
                            </motion.article>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
